import {
    OutstandingCashItem,
    TransactionForReconciliationFromStatement,
} from "@common/contracts/reconciliation.contract";
import { createContext } from "react";

export interface TransactionToMatch {
    currentlyMatchedFinancialTransactionId?: number;
    statementTransactionToMatch: TransactionForReconciliationFromStatement;
}

type CheckMergeTarget =
    | { bankTransactionId: number }
    | { statementTransactionId: string };

/**
 * A check the user paired with the line that cleared it: a bank transaction
 * already in Kick, or a statement line created when the reconciliation is
 * finished. The line's description is kept so the pairing can be shown
 * without refetching it.
 */
export type CheckMergeSelection = {
    checkTransactionId: number;
    bankDescription: string;
} & CheckMergeTarget;

export function getCheckMergeTargetKey(target: CheckMergeTarget): string {
    return "bankTransactionId" in target
        ? `transaction:${target.bankTransactionId}`
        : `statement:${target.statementTransactionId}`;
}

interface Context {
    financialTransactionsToRemoveIds: number[];
    financialTransactionsToRemoveIdsSet: Set<number>;
    notReconciledFinancialTransactionsIds: number[];
    notReconciledFinancialTransactionsIdsSet: Set<number>;
    statementTransactionsToAddIds: string[];
    statementTransactionsToAddIdsSet: Set<string>;
    financialTransactionIdsIgnoringDateChanges: number[];
    financialTransactionIdsIgnoringDateChangesSet: Set<number>;
    financialTransactionsFromPreviousPeriodIds: number[];
    transactionToMatch?: TransactionToMatch;
    checkMerges: CheckMergeSelection[];
    checkToMatch?: OutstandingCashItem;
    isReconciliationComplete: boolean;
    registerFinancialTransactionToRemove(id: number): void;
    unregisterFinancialTransactionToRemove(id: number): void;
    registerNotReconciledFinancialTransaction(id: number): void;
    unregisterNotReconciledFinancialTransaction(id: number): void;
    registerStatementTransactionToAdd(id: string): void;
    unregisterStatementTransactionToAdd(id: string): void;
    setTransactionToMatch(transactionData?: TransactionToMatch): void;
    setCheckToMatch(check?: OutstandingCashItem): void;
    registerCheckMerge(selection: CheckMergeSelection): void;
    unregisterCheckMerge(checkTransactionId: number): void;
    replaceCheckMerges(checkMerges: CheckMergeSelection[]): void;
    setIsReconciliationComplete(isReconciliationComplete: boolean): void;
    registerFinancialTransactionIdIgnoringDateChange(id: number): void;
    unregisterFinancialTransactionIdIgnoringDateChange(id: number): void;
    registerFinancialTransactionFromPreviousPeriod(id: number): void;
    unregisterFinancialTransactionFromPreviousPeriod(id: number): void;
    resetReconciliationState(): void;
}

export const ReconciliationContext = createContext<Context>({
    financialTransactionsToRemoveIds: [],
    financialTransactionsToRemoveIdsSet: new Set(),
    financialTransactionIdsIgnoringDateChanges: [],
    financialTransactionIdsIgnoringDateChangesSet: new Set(),
    notReconciledFinancialTransactionsIds: [],
    notReconciledFinancialTransactionsIdsSet: new Set(),
    statementTransactionsToAddIds: [],
    statementTransactionsToAddIdsSet: new Set(),
    financialTransactionsFromPreviousPeriodIds: [],
    checkMerges: [],
    isReconciliationComplete: false,
    registerFinancialTransactionToRemove: () => {},
    unregisterFinancialTransactionToRemove: () => {},
    registerNotReconciledFinancialTransaction: () => {},
    unregisterNotReconciledFinancialTransaction: () => {},
    registerStatementTransactionToAdd: () => {},
    unregisterStatementTransactionToAdd: () => {},
    setTransactionToMatch: () => {},
    setCheckToMatch: () => {},
    registerCheckMerge: () => {},
    unregisterCheckMerge: () => {},
    replaceCheckMerges: () => {},
    setIsReconciliationComplete: () => {},
    registerFinancialTransactionIdIgnoringDateChange: () => {},
    unregisterFinancialTransactionIdIgnoringDateChange: () => {},
    registerFinancialTransactionFromPreviousPeriod: () => {},
    unregisterFinancialTransactionFromPreviousPeriod: () => {},
    resetReconciliationState: () => {},
});
